import { useState, useCallback, useRef, useEffect } from 'react'

import useReviews from './useReviews'

const useInfinitePagination = (initialData = [], filterType = '', productSlug = '', hasMoreData = false) => {
  // Per-filter caching: store data separately for each filter type
  const cacheRef = useRef({})
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [_updateTrigger, setUpdateTrigger] = useState(0)
  const { fetchReviews } = useReviews()

  // Initialize cache for current filter if it doesn't exist
  if (!cacheRef.current[filterType]) {
    cacheRef.current[filterType] = {
      items: initialData,
      currentPage: 1,
      hasMore: hasMoreData,
      hasLoadedInitial: false,
    }
  }

  // Update cache when initialData changes
  useEffect(() => {
    const cache = cacheRef.current[filterType]

    if (cache) {
      // Case 1: First time loading this filter with server data
      if (!cache.hasLoadedInitial && initialData.length > 0) {
        cache.items = initialData
        cache.hasMore = hasMoreData
        cache.hasLoadedInitial = true
        cache.currentPage = 1
        setUpdateTrigger(prev => prev + 1)
      }
      // Case 2: Data actually changed (new review added)
      // Compare first item ID to detect real changes, not just array reference changes
      else if (
        cache.hasLoadedInitial &&
        initialData.length > 0 &&
        cache.items.length > 0 &&
        cache.items[0]?.id !== initialData[0]?.id
      ) {
        // A new review was added at the top, reset to page 1
        cache.items = initialData
        cache.hasMore = hasMoreData
        cache.currentPage = 1
        setUpdateTrigger(prev => prev + 1)
      }
    }
  }, [initialData, hasMoreData, filterType])

  const currentCache = cacheRef.current[filterType]

  const loadNextPage = useCallback(async () => {
    const cache = cacheRef.current[filterType]
    if (isLoadingMore || !cache.hasMore || !productSlug) return

    setIsLoadingMore(true)
    setError(null)

    try {
      const response = await fetchReviews(productSlug, cache.currentPage + 1, filterType)

      cacheRef.current[filterType] = {
        ...cache,
        items: [...cache.items, ...response.items],
        currentPage: cache.currentPage + 1,
        hasMore: response.hasMore,
        hasLoadedInitial: true,
      }

      setUpdateTrigger(prev => prev + 1)
    } catch (err) {
      setError('Failed to load more reviews. Please try again.')
    } finally {
      setIsLoadingMore(false)
    }
  }, [filterType, productSlug, isLoadingMore, fetchReviews])

  // Auto-load first page if cache hasn't loaded data for this filter
  useEffect(() => {
    const cache = cacheRef.current[filterType]

    if (cache && !cache.hasLoadedInitial && productSlug && !isLoadingMore) {
      const loadFirstPage = async () => {
        setIsLoadingMore(true)
        setError(null)

        try {
          const response = await fetchReviews(productSlug, 1, filterType)

          cacheRef.current[filterType] = {
            ...cache,
            items: response.items,
            currentPage: 1,
            hasMore: response.hasMore,
            hasLoadedInitial: true,
          }

          setUpdateTrigger(prev => prev + 1)
        } catch (err) {
          setError('Failed to load reviews. Please try again.')
        } finally {
          setIsLoadingMore(false)
        }
      }

      loadFirstPage()
    }
  }, [filterType, productSlug, isLoadingMore, fetchReviews])

  return {
    items: currentCache.items,
    hasMore: currentCache.hasMore,
    isLoadingMore,
    error,
    loadNextPage,
  }
}

export default useInfinitePagination
